/* ============================================================
   Lindos Club — Loyalty dedicated page enhancements
   Loaded ONLY on /apps/loyalty (see layout/theme.liquid).

   Step 1 — Full-text restore:
   Appstle truncates the earn/redeem card title & description to
   ~60 characters and appends an ellipsis ("…"). The full value
   lives in the React fiber props (memoizedProps.redeemRuleItem /
   earnRuleItem). We read it from the fiber and write it back into
   the DOM, so the merchant's complete copy is shown.
   (Mirrors the proven Wiwify Circle approach.)
   ============================================================ */
(function () {
  'use strict';

  var POLL_INTERVAL = 500;
  var MAX_ATTEMPTS = 40;
  var ELLIPSIS = '…';
  var attempts = 0;
  var CFG = window.LINDOS_LOYALTY || {};
  // honor prefers-reduced-motion in every programmatic scroll
  var SCROLL_BEHAVIOR = (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) ? 'auto' : 'smooth';

  var LIST_SELECTORS = [
    '.appstleLoyaltyPageV2WaysToRedeemList',
    '.appstleLoyaltyPageV2WaysToEarnList'
  ];

  // Appstle stores rule text with Turkish letters as literal \uXXXX escapes
  // and line breaks as literal "\n". The field also supports HTML (e.g. <br>),
  // which Appstle renders as markup. So we decode \uXXXX, drop the layout
  // newlines, and KEEP intended tags like <br> — then write via innerHTML,
  // matching exactly how Appstle renders the untruncated value.
  function decodeUnicode(raw) {
    return String(raw || '').replace(/\\u[\dA-Fa-f]{4}/g, function (m) {
      return String.fromCharCode(parseInt(m.slice(2), 16));
    });
  }

  // HTML-preserving decode (for fields that support markup, e.g. descriptions).
  function decodeHtml(raw) {
    var text = decodeUnicode(raw)
      .replace(/\\r|\r|\\n|\n/g, ' ')        // escaped or real newlines -> space
      .replace(/\s*(<br\s*\/?>)\s*/gi, '$1') // tighten whitespace around <br>
      .replace(/[ \t]{2,}/g, ' ')
      .trim();
    return text;
  }

  // Plain-text decode (for single-line fields like titles).
  function decodeText(raw) {
    var text = decodeUnicode(raw)
      .replace(/\\r|\r|\\n|\n/g, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    var tmp = document.createElement('textarea');
    tmp.innerHTML = text;
    return tmp.value;
  }

  // Walk up the React fiber tree from a card to find the rule object
  // that carries the human-readable text fields.
  function findRuleItem(node) {
    var fiberKey = Object.keys(node).find(function (k) {
      return k.indexOf('__reactFiber') === 0;
    });
    if (!fiberKey) return null;
    var fiber = node[fiberKey];
    var hops = 0;
    while (fiber && hops < 14) {
      var p = fiber.memoizedProps;
      if (p) {
        var item = p.redeemRuleItem || p.earnRuleItem || p.ruleItem || p.rule || p.item;
        if (item && (item.customerFacingLabel || item.name)) return item;
      }
      fiber = fiber.return;
      hops++;
    }
    return null;
  }

  function restoreCard(card) {
    try {
      var item = findRuleItem(card);
      if (!item) return;

      var title = card.querySelector('[class*="ListItemTitle"]');
      if (title && title.textContent.indexOf(ELLIPSIS) !== -1 && item.name) {
        title.textContent = decodeText(item.name);
      }

      var desc = card.querySelector('[class*="ListItemDescription"]');
      if (desc && desc.textContent.indexOf(ELLIPSIS) !== -1 && item.customerFacingLabel) {
        desc.innerHTML = decodeHtml(item.customerFacingLabel);
      }
    } catch (e) { /* fail safe per card */ }
  }

  function restoreAll() {
    LIST_SELECTORS.forEach(function (sel) {
      var list = document.querySelector(sel);
      if (!list) return;
      Array.prototype.slice.call(list.children).forEach(restoreCard);
    });
  }

  /* -----------------------------------------------------------
     Section replacements with artwork + mobile carousel
     (all visitors). Desktop ≥769px keeps the single wide artwork.
     Mobile ≤768px = a head artwork + a swipeable one-card-per-view
     carousel of three square cards, with arrows + dot navigation.
     Native scroll-snap does the swiping; JS only wires arrows/dots
     and keeps them in sync with the scroll position. Only one
     device's images download (the other side's <img>s are lazy
     inside a display:none branch).
     Used for: VIP tiers ("LINDOS puan") + "Nasıl Çalışır".
     ----------------------------------------------------------- */
  var VIP_SECTION = '#appstleLoyaltyPageV2VipSection';

  // VIP tier mobile slides are real HTML cards (Toygar's avantaj-karti.html template:
  // tier-colored banner strip + tier name + icon/benefit rows). Icons are the template's
  // line-style SVG library (stroke follows currentColor).
  var TIER_ICON_ATTRS = 'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
  var TIER_ICONS = {
    kaplumbaga: '<svg viewBox="0 0 48 48" ' + TIER_ICON_ATTRS + '><path d="M11 29a13 9 0 0 1 26 0z"/><path d="M24 20v9M17 21l-1.5 8M31 21l1.5 8"/><circle cx="40" cy="26" r="2.8"/><path d="M14 29l-1.5 4M21 29l-.7 4.5M27 29l.7 4.5M34 29l1.5 4"/></svg>',
    pasta: '<svg viewBox="0 0 48 48" ' + TIER_ICON_ATTRS + '><path d="M12 40V29c0-2 2-3 4-3h16c2 0 4 1 4 3v11"/><path d="M12 33c2 2 4 2 6 0s4-2 6 0 4 2 6 0 4-2 6 0"/><path d="M24 26v-5"/><path d="M24 15c1.5 1.5 1.5 3 0 4.5C22.5 18 22.5 16.5 24 15z"/><path d="M9 40h30"/></svg>',
    yildiz: '<svg viewBox="0 0 48 48" ' + TIER_ICON_ATTRS + '><path d="M18 11c1 6 3 8 9 9-6 1-8 3-9 9-1-6-3-8-9-9 6-1 8-3 9-9z"/><path d="M33 26c.6 3.5 1.5 4.5 5 5-3.5.5-4.4 1.5-5 5-.6-3.5-1.5-4.5-5-5 3.5-.5 4.4-1.5 5-5z"/></svg>',
    canta: '<svg viewBox="0 0 48 48" ' + TIER_ICON_ATTRS + '><path d="M13 20h22l-2 20H15z"/><path d="M13 25h22"/><path d="M19 20v-3a5 5 0 0 1 10 0v3"/></svg>',
    kisi: '<svg viewBox="0 0 48 48" ' + TIER_ICON_ATTRS + '><circle cx="24" cy="18" r="6.5"/><path d="M12 39a12 12 0 0 1 24 0"/></svg>',
    hediye: '<svg viewBox="0 0 48 48" ' + TIER_ICON_ATTRS + '><rect x="13" y="23" width="22" height="16" rx="1.5"/><path d="M11 20h26v5H11zM24 20v19"/><path d="M24 20c-2-5-5-6-7-4s0 4 7 4zM24 20c2-5 5-6 7-4s0 4-7 4z"/></svg>',
    kargo: '<svg viewBox="0 0 48 48" ' + TIER_ICON_ATTRS + '><path d="M6 18h18v14H6z"/><path d="M24 22h7l5 5v5h-12z"/><circle cx="14" cy="35" r="3"/><circle cx="31" cy="35" r="3"/><path d="M6 32h4M18 32h6M34 32h2"/></svg>'
  };
  var TIER_CARDS = [
    {
      name: 'Green', color: '#d0e0b3', banner: 'İlk alışverişinizden itibaren',
      benefits: [
        { icon: 'kaplumbaga', text: 'Her alışverişinizin %2’sini geri kazanın', note: '(1 puan = 1 TL)' },
        { icon: 'pasta', text: 'Doğum gününüze özel 100 puan hediye' },
        { icon: 'yildiz', text: 'Hoş geldin bonusu ile 100 puan hediye' },
        { icon: 'canta', text: '2. siparişinize 100 puan, 3. siparişinize 150 puan hediye' },
        { icon: 'kisi', text: 'Arkadaşınızı davet ettiğinizde 200 puan hediye' }
      ]
    },
    {
      name: 'Eco', color: '#c3d9ef', banner: '5.000 – 15.000 TL harcama',
      benefits: [
        { icon: 'kaplumbaga', text: 'Her alışverişinizin %5’ini geri kazanın', note: '(1 puan = 1 TL)' },
        { icon: 'pasta', text: 'Doğum gününüze özel 150 puan hediye' },
        { icon: 'canta', text: '2. siparişinize 100 puan, 3. siparişinize 150 puan hediye' },
        { icon: 'kisi', text: 'Arkadaşınızı davet ettiğinizde 200 puan hediye' },
        { icon: 'hediye', text: 'Size özel sürpriz hediyeler!' }
      ]
    },
    {
      name: 'Earth', color: '#f9e6c4', banner: '15.000 TL ve üzeri harcama',
      benefits: [
        { icon: 'kaplumbaga', text: 'Her alışverişinizin %10’unu geri kazanın', note: '(1 puan = 1 TL)' },
        { icon: 'pasta', text: 'Doğum gününüze özel 250 puan hediye' },
        { icon: 'canta', text: '2. siparişinize 100 puan, 3. siparişinize 150 puan hediye' },
        { icon: 'kisi', text: 'Arkadaşınızı davet ettiğinizde 200 puan hediye' },
        { icon: 'hediye', text: 'Size özel sürpriz hediyeler!' },
        { icon: 'kargo', text: 'Tüm siparişlerinizde ücretsiz kargo' }
      ]
    }
  ];
  function tierCardHtml(t) {
    return '<div class="lindos-tier-card" style="--lindos-tier-strip:' + t.color + '">' +
      '<div class="lindos-tier-card__banner">' + t.banner + '</div>' +
      '<div class="lindos-tier-card__title">' + t.name + '</div>' +
      '<div class="lindos-tier-card__benefits">' +
        t.benefits.map(function (b) {
          return '<div class="lindos-tier-card__benefit">' +
            '<div class="lindos-tier-card__icon" aria-hidden="true">' + TIER_ICONS[b.icon] + '</div>' +
            '<div class="lindos-tier-card__text">' + b.text +
              (b.note ? '<span class="lindos-tier-card__note">' + b.note + '</span>' : '') +
            '</div>' +
          '</div>';
        }).join('') +
      '</div>' +
    '</div>';
  }

  // "Nasıl Çalışır" mobile slides are real HTML cards (Toygar's nasil-calisir.html
  // template: title + gradient blob + number/subtitle/desc + footer bar), not images.
  var HOW_STEPS = [
    { num: '1', title: 'Hesap Oluşturun', desc: 'Aramıza katıldığınızda 100 puan kazanırsınız.' },
    { num: '2', title: 'Puan Kazanın', desc: 'Her alışverişte puan kazanırsınız.' },
    { num: '3', title: 'Puanlarınızı Kullanın', desc: 'Biriken puanlarınızı indirimlere dönüştürün.' }
  ];
  function howCardHtml(step, i, idPrefix) {
    // gradient ids must be unique per slide AND per render context (the cards
    // render twice: mobile carousel + desktop grid) — duplicate SVG ids all
    // resolve to the first instance in the document
    var gid = (idPrefix || 'lindosHowGrad') + i;
    return '<div class="lindos-how-card">' +
      '<div class="lindos-how-card__title">NASIL ÇALIŞIR</div>' +
      '<div class="lindos-how-card__blob" aria-hidden="true">' +
        '<svg viewBox="0 0 400 250" xmlns="http://www.w3.org/2000/svg">' +
          '<defs><linearGradient id="' + gid + '" x1="0%" y1="0%" x2="30%" y2="100%">' +
            '<stop offset="0%" stop-color="#b8d98a"/>' +
            '<stop offset="50%" stop-color="#a9c1e8"/>' +
            '<stop offset="100%" stop-color="#f6d99a"/>' +
          '</linearGradient></defs>' +
          '<path d="M70,40 C120,2 210,-8 285,22 C355,50 400,95 378,150 ' +
                   'C360,200 300,222 235,232 C170,242 95,246 48,208 ' +
                   'C6,174 -6,120 18,80 C34,54 48,56 70,40 Z" ' +
                'fill="url(#' + gid + ')" opacity="0.85"/>' +
        '</svg>' +
      '</div>' +
      '<div class="lindos-how-card__content">' +
        '<div class="lindos-how-card__number">' + step.num + '</div>' +
        '<div class="lindos-how-card__subtitle">' + step.title + '</div>' +
        '<div class="lindos-how-card__desc">' + step.desc + '</div>' +
      '</div>' +
      '<div class="lindos-how-card__bar"></div>' +
    '</div>';
  }
  var CHEVRON_PREV = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>';
  var CHEVRON_NEXT = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>';

  function setupArtCarousel(root) {
    var viewport = root.querySelector('.lindos-art-carousel__viewport');
    var slides = Array.prototype.slice.call(root.querySelectorAll('.lindos-art-carousel__slide'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('.lindos-art-carousel__dot'));
    var prev = root.querySelector('.lindos-art-carousel__arrow--prev');
    var next = root.querySelector('.lindos-art-carousel__arrow--next');
    if (!viewport || !slides.length) return;

    function current() {
      // clientWidth is 0 while the carousel is display:none (desktop)
      var w = viewport.clientWidth || 1;
      var i = Math.round(viewport.scrollLeft / w);
      return Math.max(0, Math.min(slides.length - 1, i));
    }
    function update() {
      var i = current();
      dots.forEach(function (d, j) {
        d.classList.toggle('is-active', j === i);
        d.setAttribute('aria-current', j === i ? 'true' : 'false');
      });
      if (prev) prev.disabled = (i === 0);
      if (next) next.disabled = (i === slides.length - 1);
    }
    function goTo(i) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      viewport.scrollTo({ left: i * viewport.clientWidth, behavior: SCROLL_BEHAVIOR });
    }

    if (prev) prev.addEventListener('click', function () { goTo(current() - 1); });
    if (next) next.addEventListener('click', function () { goTo(current() + 1); });
    dots.forEach(function (d, j) {
      d.addEventListener('click', function () { goTo(j); });
    });

    var raf;
    viewport.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  // Generic builder: hides the Appstle container, appends the desktop layout
  // + mobile carousel (optionally with a head), then wires the controls.
  // Every visual part is either an image or ready-made HTML: desktop
  // (desktop src + desktopAlt | desktopHtml), mobile head (head + headAlt |
  // headHtml) and slides (slides[] srcs + slideAlts[] | slidesHtml[] — real
  // text, so no alts needed).
  // o: {section, container, artClass, desktop?, desktopAlt, desktopHtml?,
  //     head?, headAlt, headHtml?, slides[]?, slideAlts[]?, slidesHtml[]?,
  //     dotLabels[], regionLabel}
  function initArtCarousel(o) {
    var count = o.slidesHtml ? o.slidesHtml.length : (o.slides || []).length;
    if (!(o.desktop || o.desktopHtml) || !count) return;
    var section = document.querySelector(o.section);
    if (!section) return;

    // Hide first, then check for the banner: Appstle re-renders can
    // rebuild the container while our appended banner survives.
    var container = section.querySelector(o.container);
    if (container) container.style.setProperty('display', 'none', 'important');

    if (section.querySelector('.' + o.artClass)) return;

    var slidesHtml = o.slidesHtml
      ? o.slidesHtml.map(function (html) {
          return '<li class="lindos-art-carousel__slide">' + html + '</li>';
        }).join('')
      : o.slides.map(function (src, i) {
          return '<li class="lindos-art-carousel__slide">' +
            '<img src="' + src + '" loading="lazy" alt="' + (o.slideAlts[i] || '') + '">' +
          '</li>';
        }).join('');
    var dotsHtml = '';
    for (var i = 0; i < count; i++) {
      dotsHtml += '<button type="button" class="lindos-art-carousel__dot" aria-label="' +
        (o.dotLabels[i] || (i + 1)) + '"></button>';
    }

    var banner = document.createElement('div');
    banner.className = 'lindos-section-art ' + o.artClass;
    banner.innerHTML =
      (o.desktopHtml
        ? '<div class="lindos-art-carousel__desktop-html">' + o.desktopHtml + '</div>'
        : '<img class="lindos-art-carousel__desktop" src="' + o.desktop + '" loading="lazy" alt="' + o.desktopAlt + '">') +
      '<div class="lindos-art-carousel" role="region" aria-label="' + o.regionLabel + '">' +
        (o.headHtml
          ? o.headHtml
          : (o.head ? '<img class="lindos-art-carousel__head" src="' + o.head + '" loading="lazy" alt="' + o.headAlt + '">' : '')) +
        '<div class="lindos-art-carousel__stage">' +
          '<div class="lindos-art-carousel__viewport">' +
            '<ul class="lindos-art-carousel__track">' + slidesHtml + '</ul>' +
          '</div>' +
          '<button type="button" class="lindos-art-carousel__arrow lindos-art-carousel__arrow--prev" aria-label="Önceki">' + CHEVRON_PREV + '</button>' +
          '<button type="button" class="lindos-art-carousel__arrow lindos-art-carousel__arrow--next" aria-label="Sonraki">' + CHEVRON_NEXT + '</button>' +
        '</div>' +
        '<div class="lindos-art-carousel__dots">' + dotsHtml + '</div>' +
      '</div>';
    section.appendChild(banner);
    setupArtCarousel(banner.querySelector('.lindos-art-carousel'));
  }

  // Section head ("LINDOS puan" + tagline) — shared by the mobile carousel and
  // the desktop grid, so the whole tier section is image-free HTML.
  var TIER_HEAD_HTML =
    '<div class="lindos-tier-head">' +
      '<h2 class="lindos-tier-head__title"><span class="lindos-brand-wordmark" role="img" aria-label="LINDOS"></span><span>puan</span></h2>' +
      '<p class="lindos-tier-head__sub">Üye olun, her alışverişinizde puan biriktirin. Biriken puanlarınızı dilediğiniz zaman indirim olarak kullanın!</p>' +
    '</div>';

  function initTierBanner() {
    initArtCarousel({
      section: VIP_SECTION,
      container: '.appstleLoyaltyPageV2VipSectionContainer',
      artClass: 'lindos-tier-banner',
      // Desktop ≥769px: same head + the three cards side by side (the retired
      // tiers-desktop.webp had exactly this layout, baked into one image).
      desktopHtml: TIER_HEAD_HTML +
        '<div class="lindos-tier-grid">' +
          TIER_CARDS.map(function (t) {
            return '<div class="lindos-tier-grid__cell">' + tierCardHtml(t) + '</div>';
          }).join('') +
        '</div>',
      headHtml: TIER_HEAD_HTML,
      slidesHtml: TIER_CARDS.map(tierCardHtml),
      dotLabels: ['1. seviye: Green', '2. seviye: Eco', '3. seviye: Earth'],
      regionLabel: 'Lindos Puan üyelik seviyeleri'
    });
  }

  function initHowBanner() {
    // "Nasıl Çalışır" is shown to logged-out visitors only. For logged-in
    // customers the whole section is hidden (Toygar, 2026-07-22).
    if (CFG.loggedIn) {
      var howSection = document.querySelector('#appstleLoyaltyPageV2HowItWorks');
      if (howSection) howSection.style.setProperty('display', 'none', 'important');
      return;
    }
    initArtCarousel({
      section: '#appstleLoyaltyPageV2HowItWorks',
      container: '.appstleLoyaltyPageV2HowItWorksContainer',
      artClass: 'lindos-how-banner',
      // Desktop/tablet ≥1024px: section head + the three cards side by side
      // (cards hide their internal title there — the head carries it).
      desktopHtml:
        '<div class="lindos-tier-head lindos-how-head"><h2 class="lindos-tier-head__title">NASIL ÇALIŞIR</h2></div>' +
        '<div class="lindos-how-grid">' +
          HOW_STEPS.map(function (s, i) {
            return '<div class="lindos-how-grid__cell">' + howCardHtml(s, i, 'lindosHowGradD') + '</div>';
          }).join('') +
        '</div>',
      slidesHtml: HOW_STEPS.map(function (s, i) { return howCardHtml(s, i, 'lindosHowGradM'); }),
      dotLabels: ['1. adım: Hesap Oluşturun', '2. adım: Puan Kazanın', '3. adım: Puanlarınızı Kullanın'],
      regionLabel: 'Nasıl çalışır adımları'
    });
  }

  // Hide the "Rewards powered by Appstle" footer row. Appstle injects its
  // CSS after the theme stylesheet and with !important, so a stylesheet rule
  // loses the cascade — an inline !important style wins regardless of order.
  function hidePoweredBy() {
    Array.prototype.slice.call(document.querySelectorAll('.loyalty-brand-info-text')).forEach(function (s) {
      var row = s.parentElement || s;
      row.style.setProperty('display', 'none', 'important');
    });
  }

  /* -----------------------------------------------------------
     Hero banner (all visitors).
     Replaces the Appstle hero with the "LINDOS puan" welcome
     artwork — one per device × login state (theme.liquid picks
     the in/out asset pair). Logged-in: the customer's point
     balance is read from the hero heading (.al-loyalty-money-value)
     BEFORE the hero is hidden and rendered into the artwork's
     dashed "BİRİKMİŞ LINDOS PUANLARIN" box; the drawn "alışverişe
     başla!" button links to the shop. Logged-out: invisible
     hotspots over the drawn "giriş yap" / "üye ol" pills.
     Overlay positions are % of each artwork (lindos-loyalty.css),
     measured on the masters (desktop 4600×1840, mobile 1080×1080).
     ----------------------------------------------------------- */
  var HERO_SELECTOR = '.appstleLoyaltyPageV2HeroContainer, #appstleLoyaltyPageV2HeroSection';
  var heroPoints = null; // cached — survives Appstle re-renders that rebuild the hero

  // "176.00" / "1,250.00" / "1.250,00" → number (Appstle prints shop money format)
  function parseMoney(t) {
    t = String(t).replace(/[^\d.,]/g, '');
    if (!t) return NaN;
    var dec = Math.max(t.lastIndexOf('.'), t.lastIndexOf(','));
    if (dec === -1) return parseFloat(t);
    var intPart = t.slice(0, dec).replace(/[.,]/g, '');
    var frac = t.slice(dec + 1);
    if (frac.length === 3) return parseFloat(intPart + frac); // thousands sep, no decimals
    return parseFloat(intPart + '.' + frac);
  }

  function readHeroPoints() {
    if (heroPoints !== null) return heroPoints;
    var heading = document.querySelector('.appstleLoyaltyPageV2HeroCustomerHeading');
    if (!heading) return null; // hero not rendered yet — try again on a later pass
    var el = heading.querySelector('.al-loyalty-money-value');
    var num = el ? parseMoney(el.textContent) : 0; // heading without a number = zero balance
    if (isNaN(num)) num = 0;
    heroPoints = num.toLocaleString('tr-TR', { maximumFractionDigits: num % 1 ? 2 : 0 }) + ' puan';
    return heroPoints;
  }

  // Points left until the next tier (tier art: "ECO/EARTH üye olmanıza X puan
  // kaldı!"). Tiers run on the customer's STORE CREDIT balance (1 puan = 1 TL):
  // < 5.000 GREEN, 5.000–15.000 ECO, 15.000+ EARTH. The balance comes from
  // Liquid (CFG.storeCredit) so this is synchronous and matches the tier that
  // picked the artwork server-side.
  var TIER_MILESTONES = { green: 5000, eco: 15000 };
  function readNextTierPoints() {
    var milestone = TIER_MILESTONES[CFG.heroTier];
    if (!milestone) return null; // EARTH (top tier) — its art has no such line
    var credit = Number(CFG.storeCredit) || 0;
    return Math.max(0, Math.ceil(milestone - credit)).toLocaleString('tr-TR');
  }

  function heroOverlaysHtml() {
    if (CFG.loggedIn) {
      var html = '<span class="lindos-hero__points"></span>' +
        '<a class="lindos-hero__hotspot lindos-hero__hotspot--shop" href="/collections/all">' +
          '<span class="visually-hidden">Alışverişe başla</span></a>';
      if (CFG.heroLayout === 'tier' && CFG.heroTier !== 'earth') {
        html += '<span class="lindos-hero__nextpts" aria-hidden="true"></span>' +
          '<a class="lindos-hero__hotspot lindos-hero__hotspot--tiers" href="#appstleLoyaltyPageV2VipSection">' +
            '<span class="visually-hidden">Üyelik seviyelerini ve fırsatlarını incele</span></a>';
      }
      return html;
    }
    return '<a class="lindos-hero__hotspot lindos-hero__hotspot--login" ' +
        'href="/account/login?return_url=%2Fapps%2Floyalty">' +
        '<span class="visually-hidden">Giriş yap</span></a>' +
      '<a class="lindos-hero__hotspot lindos-hero__hotspot--register" href="/account/register">' +
        '<span class="visually-hidden">Üye ol</span></a>';
  }

  // Guest hero — real HTML stage (Toygar's puan-afisi.html template: white card,
  // brand block, subtitle/body, "1 PUAN = 1 TL" badge, products illustration,
  // login/register pills). Mobil tek sütun; ≥1024 iki sütun (metin sol, DİKEY
  // illüstrasyon + altında rozet sağda — üye kartıyla aynı desen).
  // 2026-07-27 (Toygar): şablondaki degrade blob SVG'si TAMAMEN kaldırıldı
  // ("bu renkli path hiçbir ekranda olmasın"); üye kartındaki kardeşi de aynı
  // turda kalktı. b1/b2'deki position:relative + z-index:1 artık gereksiz ama
  // zararsız olduğu için CSS'te bırakıldı.
  function heroStageHtml() {
    return '<div class="lindos-hero-stage">' +
      '<div class="lindos-hero-stage__brand">' +
        '<div class="lindos-hero-stage__b1"><span class="lindos-brand-wordmark" role="img" aria-label="LINDOS"></span></div>' +
        '<div class="lindos-hero-stage__b2"><span class="lindos-hero-stage__puan">puan</span><span class="lindos-hero-stage__tag">ile tanıştın mı?</span></div>' +
      '</div>' +
      '<div class="lindos-hero-stage__subtitle">Siparişleriniz Lindos puanlarla size geri dönüyor!</div>' +
      '<div class="lindos-hero-stage__body">Alışveriş yaptıkça puan kazanın, puanlarınızı biriktirin ve dilediğiniz zaman indirime dönüştürün. Hem de çok basit, nasıl mı?</div>' +
      '<div class="lindos-hero-stage__badge">1 PUAN = 1 TL</div>' +
      '<div class="lindos-hero-stage__illus" role="img" aria-label="El ele tutuşan Lindos ürünleri"></div>' +
      '<div class="lindos-hero-stage__prompt">Hemen kazanmaya başlamak için:</div>' +
      '<div class="lindos-hero-stage__btns">' +
        '<a href="/account/login?return_url=%2Fapps%2Floyalty">giriş yap</a>' +
        '<a href="/account/register">üye ol</a>' +
      '</div>' +
    '</div>';
  }

  // Logged-in hero — real HTML member card (Toygar's uye-karti.html template:
  // brand block over a blob + "1 PUAN = 1 TL" badge + tier pill + accumulated
  // points with a shop CTA + next-tier teaser). One card for BOTH devices,
  // centered at ≤600px. Tier comes from CFG.heroTier (server-side, store-credit
  // based); ECO/EARTH variants derive from the template's GREEN card, pill
  // colors matching the tier cards. EARTH (top tier) has no next-tier row.
  var HERO_TIERS = {
    green: { label: 'GREEN ÜYE', bg: '#c7d5b4', nextName: 'ECO' },
    eco:   { label: 'ECO ÜYE',   bg: '#c3d9ef', nextName: 'EARTH' },
    earth: { label: 'EARTH ÜYE', bg: '#f9e6c4', nextName: null }
  };
  function heroMemberHtml(tier) {
    var t = HERO_TIERS[tier] || HERO_TIERS.green;
    return '<div class="lindos-hero-member" style="--lindos-member-tier:' + t.bg + '">' +
      '<div class="lindos-hero-member__brand">' +
        '<div class="lindos-hero-member__b1"><span class="lindos-brand-wordmark" role="img" aria-label="LINDOS"></span></div>' +
        '<div class="lindos-hero-member__b2"><span class="lindos-hero-member__puan">puan</span><span class="lindos-hero-member__tail">’a hoş geldiniz!</span></div>' +
      '</div>' +
      '<div class="lindos-hero-member__badge">1 PUAN = 1 TL</div>' +
      '<div class="lindos-hero-member__tier">' + t.label + '</div>' +
      '<div class="lindos-hero-member__points">' +
        '<div class="lindos-hero-member__points-left">' +
          '<div class="lindos-hero-member__plabel">Birikmiş Lindos puanınız:</div>' +
          '<div class="lindos-hero-member__pvalue"></div>' +
        '</div>' +
        '<a class="lindos-hero-member__cta" href="/collections/all">alışverişe<br>başlayın!</a>' +
      '</div>' +
      (t.nextName
        ? '<div class="lindos-hero-member__next">' +
            '<svg class="lindos-hero-member__arrow" viewBox="0 0 44 52" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
              '<path d="M8 46 C6 26 14 12 30 8"/><path d="M20 6 L32 6 L32 18"/>' +
            '</svg>' +
            '<div class="lindos-hero-member__next-text">' +
              '<div class="lindos-hero-member__nbold">' + t.nextName + ' üye olmanıza <span class="lindos-hero-member__remaining"><span class="lindos-hero-member__rvalue"></span> puan kaldı!</span></div>' +
              '<a class="lindos-hero-member__nlink" href="#appstleLoyaltyPageV2VipSection">' + t.nextName + ' fırsatlarını keşfedin.</a>' +
            '</div>' +
          '</div>'
        : '') +
      // Product illustration — shown only in the ≥1024px two-column layout (right
      // column), mirroring the guest hero; hidden on mobile via CSS.
      '<div class="lindos-hero-member__illus" aria-hidden="true"></div>' +
    '</div>';
  }

  function initHeroBanner() {
    if (!CFG.heroDesktop || !CFG.heroMobile) return;

    // Never hide the hero before the balance can be read from it.
    var points = CFG.loggedIn ? readHeroPoints() : '';
    if (CFG.loggedIn && points === null) return;

    var hero = document.querySelector(HERO_SELECTOR);
    // Inline !important backup — Appstle's CSS loads late with !important,
    // the stylesheet rule alone can lose the cascade.
    if (hero) hero.style.setProperty('display', 'none', 'important');

    if (!document.querySelector('.lindos-hero')) {
      if (!hero) return;
      var alt = CFG.loggedIn
        ? 'Lindos Puan’a hoşgeldin! Siparişlerin Lindos puanlarla sana geri dönüyor: alışveriş yaptıkça puan kazan, puanlarını biriktir ve dilediğin zaman indirime dönüştür. 1 puan = 1 TL.'
        : 'Lindos Puan ile tanıştın mı? Siparişlerin Lindos puanlarla sana geri dönüyor: alışveriş yaptıkça puan kazan, puanlarını biriktir ve dilediğin zaman indirime dönüştür. 1 puan = 1 TL. Hemen kazanmaya başlamak için giriş yap veya üye ol.';
      var banner = document.createElement('div');
      banner.className = 'lindos-hero' +
        (CFG.heroLayout === 'tier' ? ' lindos-hero--tier lindos-hero--' + CFG.heroTier : '');
      banner.innerHTML = CFG.loggedIn
        ? '<div class="lindos-hero-member-wrap">' + heroMemberHtml(CFG.heroTier) + '</div>'
        : '<div class="lindos-hero-stage-wrap">' + heroStageHtml() + '</div>';
      hero.parentNode.insertBefore(banner, hero);
      // "… fırsatlarını keşfedin." → smooth-scroll to the tier section on this page
      Array.prototype.slice.call(banner.querySelectorAll('.lindos-hero-member__nlink')).forEach(function (a) {
        a.addEventListener('click', function (ev) {
          var target = document.querySelector('#appstleLoyaltyPageV2VipSection');
          if (target) { ev.preventDefault(); target.scrollIntoView({ behavior: SCROLL_BEHAVIOR }); }
        });
      });
    }

    if (CFG.loggedIn && points) {
      // card label already says "puanınız" — show the bare number
      var pv = document.querySelector('.lindos-hero-member__pvalue');
      var num = points.replace(/\s*puan$/, '');
      if (pv && pv.textContent !== num) pv.textContent = num;
    }
    if (CFG.loggedIn && (HERO_TIERS[CFG.heroTier] || HERO_TIERS.green).nextName) {
      var next = readNextTierPoints(); // synchronous — store credit comes from Liquid
      if (next !== null) {
        var rv = document.querySelector('.lindos-hero-member__rvalue');
        if (rv && rv.textContent !== next) rv.textContent = next;
      }
    }
  }

  /* -----------------------------------------------------------
     Referral banner (all visitors).
     Replaces Appstle's "Yönlendirmeler" block with the
     "Arkadaşını davet et, birlikte kazanın!" artwork — one per
     device × login state (theme.liquid picks the in/out pair).
     Logged-in: the customer's referral link is read from
     Appstle's readonly input BEFORE the block is hidden, then
     shown inside the artwork's drawn "Davet bağlantınızı
     paylaşın" field (the copy icon is part of the drawing);
     clicking the field copies the link, with ✓ feedback.
     Logged-out: static artwork, no link field in the drawing.
     Field positions are % of each artwork, pixel-measured on the
     masters (desktop 4600×1840, mobile 1080×1080).
     ----------------------------------------------------------- */
  var referralLink = ''; // cached — survives Appstle re-renders that rebuild the input
  // Grace window for a logged-in customer's link to load before we conclude they
  // have no referral link at all (no Appstle loyalty record yet → no code exists).
  var referralGraceStart = 0;
  var REFERRAL_LINK_GRACE_MS = 5000;

  function legacyCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) { /* noop */ }
    ta.remove();
  }

  function copyFeedback(btn) {
    var span = btn.querySelector('.lindos-referral-link__text');
    if (!span || btn.classList.contains('is-copied')) return;
    var original = span.textContent;
    btn.classList.add('is-copied');
    span.textContent = '✓ Bağlantı kopyalandı';
    setTimeout(function () {
      span.textContent = original;
      btn.classList.remove('is-copied');
    }, 1800);
  }

  function referralLinkHtml() {
    return '<button type="button" class="lindos-referral-link" title="Bağlantıyı kopyala">' +
      '<span class="lindos-referral-link__text" aria-live="polite"></span>' +
    '</button>';
  }

  function copyToClipboard(text, onDone) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onDone, function () {
        legacyCopy(text);
        onDone();
      });
    } else {
      legacyCopy(text);
      onDone();
    }
  }

  // Mobile referral "afiş" — real HTML card (Toygar's davet-afisi.html template:
  // sky-gradient stage + bottles artwork at the bottom + frosted panel).
  // Three modes: 'guest' = login/register pills; 'link' = click-to-copy link box
  // (member with a referral link); 'nolink' = member whose Appstle loyalty record
  // (and therefore referral code) doesn't exist yet → an "alışverişe başla" CTA
  // that leads to the shopping activity which generates their link.
  var COPY_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
  function referralStageFooter(mode) {
    if (mode === 'link') {
      return '<div class="lindos-referral-stage__prompt">Davet bağlantınızı paylaşın</div>' +
        '<button type="button" class="lindos-referral-stage__linkbox" title="Bağlantıyı kopyala">' +
          '<span class="lindos-referral-stage__link" aria-live="polite"></span>' +
          '<span class="lindos-referral-stage__copy">' + COPY_ICON + '</span>' +
        '</button>';
    }
    if (mode === 'nolink') {
      return '<div class="lindos-referral-stage__prompt">Size özel davet bağlantınızı oluşturmak için</div>' +
        '<div class="lindos-referral-stage__btns">' +
          '<a href="/collections/all">alışverişe başla</a>' +
        '</div>';
    }
    return '<div class="lindos-referral-stage__prompt">Size özel davet bağlantınıza erişmek için</div>' +
      '<div class="lindos-referral-stage__btns">' +
        '<a href="/account/login">giriş yap</a>' +
        '<a href="/account/register">üye ol</a>' +
      '</div>';
  }
  function referralStageHtml(mode) {
    return '<div class="lindos-referral-stage">' +
      '<div class="lindos-referral-stage__panel">' +
        '<div class="lindos-referral-stage__headline">Arkadaşınızı davet edin,<br><span>Birlikte kazanın!</span></div>' +
        '<div class="lindos-referral-stage__cols">' +
          '<div class="lindos-referral-stage__col"><h3>Arkadaşınız</h3><p>İlk siparişine özel %10 indirim kazanır.</p></div>' +
          '<div class="lindos-referral-stage__col"><h3>Siz</h3><p>Arkadaşınızın ilk siparişinden sonra 200 TL indirim kazanırsınız.</p></div>' +
        '</div>' +
        referralStageFooter(mode) +
      '</div>' +
    '</div>';
  }

  function initReferralBanner() {
    var section = document.getElementById('appstleLoyaltyPageV2ReferralSection');
    if (!section) return;

    if (CFG.loggedIn && !referralLink) {
      // Primary source: Appstle's inline config. customerLoyalty.referralLink is
      // present as soon as _ALConfig.customerLoyalty loads and does NOT depend on
      // the referral section rendering its readonly <input> — that input is
      // absent/late for new/0-point customers, which used to make us bail and
      // leave Appstle's default UI showing. Fallback: scrape the input.
      var cfgLink = window._ALConfig && window._ALConfig.customerLoyalty && window._ALConfig.customerLoyalty.referralLink;
      if (cfgLink && /referral\.appstle/.test(cfgLink)) {
        referralLink = String(cfgLink).trim();
      } else {
        var input = section.querySelector('input[value*="referral.appstle"]');
        if (input && input.value) referralLink = input.value.trim();
      }
    }
    // Decide which banner variant to render.
    //  • guest  → login/register pills
    //  • link   → member with a referral link → click-to-copy box
    //  • nolink → member with NO link: Appstle has no loyalty record for them yet
    //    (customerLoyalty null + /customer-loyalty API "Customer not found" + no
    //    input), so no referral code exists → show our card with an "alışverişe
    //    başla" CTA. Guard with a grace window first: a genuine link can load
    //    late, so keep Appstle's UI briefly and only conclude "no link" once the
    //    grace elapses (poll()/observer keep re-running enhance() meanwhile).
    var mode;
    if (!CFG.loggedIn) {
      mode = 'guest';
    } else if (referralLink) {
      mode = 'link';
    } else {
      if (!referralGraceStart) referralGraceStart = Date.now();
      if (Date.now() - referralGraceStart < REFERRAL_LINK_GRACE_MS) return;
      mode = 'nolink';
    }

    if (section.querySelector('.lindos-referral-banner')) return;

    var wrap = document.createElement('div');
    wrap.className = 'lindos-referral-banner';
    wrap.innerHTML =
      '<div class="lindos-referral-stage-wrap">' + referralStageHtml(mode) + '</div>';
    section.appendChild(wrap);

    // Hide Appstle's default ONLY now that our banner is in the DOM — atomic with
    // the append, so the section is never blank (the earlier bug: an unconditional
    // hide blanked the section whenever our banner failed to append in a cold-
    // session race). A CSS :has(.lindos-referral-banner) rule reinforces this and
    // also re-hides the container if Appstle rebuilds it after our banner exists.
    var container = section.querySelector('.appstleLoyaltyPageV2ReferralSectionContainer');
    if (container) container.style.setProperty('display', 'none', 'important');
    if (mode !== 'link') return;

    var display = referralLink.replace(/^https?:\/\//, '');

    // White pill link box — the whole box copies
    var box = wrap.querySelector('.lindos-referral-stage__linkbox');
    if (box) {
      var linkSpan = box.querySelector('.lindos-referral-stage__link');
      linkSpan.textContent = display;
      box.addEventListener('click', function () {
        if (box.classList.contains('is-copied')) return;
        copyToClipboard(referralLink, function () {
          box.classList.add('is-copied');
          linkSpan.textContent = '✓ Bağlantı kopyalandı';
          setTimeout(function () {
            linkSpan.textContent = display;
            box.classList.remove('is-copied');
          }, 1800);
        });
      });
    }
  }

  /* -----------------------------------------------------------
     Mobile-only interlude (logged-in), inserted between İşlem
     geçmişi and Nasıl Çalışır — real HTML stage (Toygar's
     puan-mobil.html template: products illustration + uppercase
     teaser + body + "1 PUAN = 1 TL" badge + orange bottom bar;
     its "nasıl mı?" leads into the how-it-works section).
     Hidden ≥769px by CSS. The illustration is the same asset the
     guest hero uses (byte-identical in the template).
     ----------------------------------------------------------- */
  function initInterlude() {
    if (!CFG.loggedIn) return;
    if (document.querySelector('.lindos-interlude')) return;
    var ref = document.querySelector('#appstleLoyaltyPageV2TransactionHistorySection');
    if (!ref) return;
    var el = document.createElement('div');
    el.className = 'lindos-interlude';
    el.innerHTML =
      '<div class="lindos-interlude-stage">' +
        '<div class="lindos-interlude-stage__illus" role="img" aria-label="El ele tutuşan Lindos ürünleri"></div>' +
        '<div class="lindos-interlude-stage__subtitle">Siparişleriniz Lindos puanlarla size geri dönüyor!</div>' +
        '<div class="lindos-interlude-stage__body">Alışveriş yaptıkça puan kazanın, puanlarınızı biriktirin ve dilediğiniz zaman indirime dönüştürün. Hem de çok basit, nasıl mı?</div>' +
        '<div class="lindos-interlude-stage__badge">1 PUAN = 1 TL</div>' +
      '</div>';
    ref.insertAdjacentElement('afterend', el);
  }

  function enhance() {
    // Isolate each step so one failing enhancement can't block the others
    // (notably keeps initReferralBanner running even if an earlier step throws
    // on a transient DOM during Appstle's progressive render).
    [restoreAll, initTierBanner, initHowBanner, hidePoweredBy,
     initHeroBanner, initReferralBanner, initInterlude].forEach(function (fn) {
      try { fn(); } catch (e) { /* fail safe per step */ }
    });
  }

  function anyReady() {
    if (document.querySelector(HERO_SELECTOR)) return true;
    if (document.querySelector(VIP_SECTION)) return true;
    return LIST_SELECTORS.some(function (sel) {
      var l = document.querySelector(sel);
      return l && l.children.length > 0;
    });
  }

  function poll() {
    // Keep re-running as sections stream in: Appstle renders progressively and
    // the referral section arrives LAST, so a single pass on first-ready misses
    // it. enhance() is idempotent, so re-running is safe and cheap. The
    // MutationObserver below still handles later re-renders for the session.
    if (anyReady()) enhance();
    attempts++;
    if (attempts < MAX_ATTEMPTS) setTimeout(poll, POLL_INTERVAL);
  }

  function start() { setTimeout(poll, 1000); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Appstle re-renders reset our changes; re-run on mutation (debounced).
  // Idempotent: text restore only touches cards still showing an ellipsis,
  // and the slider skips a list that is already wrapped — so this never loops.
  var pending;
  var observer = new MutationObserver(function () {
    clearTimeout(pending);
    pending = setTimeout(enhance, 300);
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
